import Column from '@components/Column';
import Row from '@components/Row';
import Text from '@components/Text';
import colors from '@constants/colors';
import styled from '@emotion/styled';
import usePriceUnit from '@hooks/item/usePriceUnit';
import XLine from '@icons/XLine';
import { OrderLine } from '@models/order';
import { pressActionEffect } from '@styles/mixin';
import { useTranslation } from 'react-i18next';

import useModifyCartItem from './hooks/useModifyCartItem';

interface Props {
    item: OrderLine;
}

function CartContent({ item }:Props) {
    const {
        itemName, status, defaultOptions, personalOptions, totalAmount,
    } = item;
    const { t } = useTranslation('cart');
    const { handleDelete } = useModifyCartItem(item);
    const options = [...personalOptions, ...defaultOptions]
        .map((optionGroup) => optionGroup.options)
        .flat();
    const isSoldOut = status === 'SOLD_OUT';
    const priceUnit = usePriceUnit();
    return (
        <TextContainer fullWidth spacing={2}>
            <Row fullWidth justify='space-between' spacing={2}>
                <NameText size='title'
                    fontWeight='medium'
                    color={isSoldOut ? colors.textUnselected : undefined}
                    ellipsis>
                    {itemName}
                </NameText>
                <DeleteButton type='button'
                    aria-label={t('item.delete')}
                    onClick={handleDelete}>
                    <XLine size={16}
                        color='var(--color-icon-unselected)'/>
                </DeleteButton>
            </Row>
            <Text size='body-long'
                color={isSoldOut ? colors.textUnselected : colors.textSecondary}>
                {options.map((option) => {
                    if (option.optionPrice) {
                        return `${option.optionName}(${option.quantity}${t('item.optionCount')})(+${option.optionPrice.toLocaleString()}${priceUnit})`;
                    }
                    return `${option.optionName}(${option.quantity}${t('item.optionCount')})`;
                }).sort().join(' | ')}
            </Text>
            <Text fontWeight='medium'
                color={isSoldOut ? colors.textUnselected : undefined}>
                {totalAmount.toLocaleString()}{priceUnit}
            </Text>
        </TextContainer>
    );
}

const TextContainer = styled(Column)`
    flex: 1;
    min-width: 0;
`;

const NameText = styled(Text)`
    flex: 1;
`;

const DeleteButton = styled.button`
    background: none;
    display: flex;
    padding: 0;
    flex-shrink: 0;
    ${pressActionEffect}
`;

export default CartContent;
